import { useRef } from "react";
import { Button } from "@/components/ui/button";
import { Camera } from "@phosphor-icons/react";

/**
 * CameraCaptureButton — one-tap "Take photo" using the native OS camera.
 *
 * On mobile browsers, `capture="environment"` opens the rear camera directly
 * so users can shoot a bill / receipt without going through the gallery. On
 * desktop it falls back to the file picker (same UX as the regular Upload).
 *
 * Props:
 *   onFile(file: File)   — called with the captured/selected image
 *   label                — button text ("Take photo" by default)
 *   disabled             — disable the button
 *   testid               — data-testid for tests
 *   accept               — override MIME filter (default: image/*)
 */
export default function CameraCaptureButton({
    onFile, label = "Take photo", disabled = false,
    testid = "camera-capture-btn", accept = "image/*",
}) {
    const inputRef = useRef(null);
    const handleChange = (e) => {
        const f = e.target.files?.[0];
        if (f) onFile?.(f);
        // Clear so the same photo can be re-picked
        if (inputRef.current) inputRef.current.value = "";
    };
    return (
        <>
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                capture="environment"
                onChange={handleChange}
                className="hidden"
                data-testid={`${testid}-input`}
            />
            <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={disabled}
                onClick={() => inputRef.current?.click()}
                data-testid={testid}
                className="gap-1.5"
            >
                <Camera size={14} weight="duotone" /> {label}
            </Button>
        </>
    );
}
